import { Container, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { CountryEnum } from "../../_common/enum/country.enum";
import { IUserRegister } from "../../_common/interface/users/iUserRegister";
import { PrimaryButton } from "../../_components/button/primary.button";
import { TextValidatorHcs } from "../../_components/textValidator/textValidatorHcs";
import { toastr } from "../../_components/toastr/toastr";
import { PRIMARY_COLOR } from "../../_const/color.const";
import { RESET_PASSWORD_ERROR_MESSAGES, RESET_PASSWORD_HELPER_TEXT, RESET_PASSWORD_VALIDATORS } from "../../_const/security.const";
import { emailRegex, phoneRegex } from "../../_const/validation.const";
import { accountService } from "../../_services/accounts.service";
import { useNavigate } from "react-router-dom";
import { handleErrorResponse } from "../../_helpers/handleResponse.helper";


export function UserRegister() {

    const resetData = () => ({
        id: "",
        firstName: '',
        lastName: '',
        name: '',
        email: '',
        phone: '',
        country: 'Ireland',
        address: '',
        county: '',
        postalCode: '',
        username: null,
        password: '',
        confirmPassword: '',
        roles: [],
        dateOfBirth: "",
        role: 'User'
    });

    const [isSaving, setIsSaving] = useState(false);
    const [data, setData] = useState<IUserRegister>(resetData);
    const navigate = useNavigate();

    useEffect(() => {
        ValidatorForm.addValidationRule('isPhoneNumber', (value) => {
            return phoneRegex.test(value);
        });
        ValidatorForm.addValidationRule('isEmailAddress', (value) => {
            return emailRegex.test(value);
        });

        return () => {
            ValidatorForm.removeValidationRule('isPhoneNumber');
            ValidatorForm.removeValidationRule('isEmailAddress');
        };
    }, []);
    function submit(e: any) {
        e.preventDefault();
        setIsSaving(true);
        saveUser();
    }

    function saveUser() {

        var _data = { ...data, name: data.firstName + data.lastName };
        _data.roles.push("User");

        accountService
            .createUser(_data)
            .then(
                (d) => {
                    toastr.success("Account Created Successfully");
                    resetData();
                    navigate("../");
                }, handleErrorResponse
            ).finally(() => setIsSaving(false));
        ;
    }

    function handleChange(event: any) {
        setData((__data) => { return { ...__data, [event.target.name]: event.target.value } });
    };


    return (
        <Container>

            <ValidatorForm
                onSubmit={submit}
                noValidate>

                <Typography textAlign="center" color={PRIMARY_COLOR[0]} variant="h4" m={1}>Create an account</Typography>

                <Grid container spacing={1}>

                    <Grid item xs={12} md={6}>
                        <TextValidatorHcs
                            key="firstName"
                            name="firstName"
                            label="First Name"
                            required
                            fullWidth
                            validators={['required', 'maxStringLength:50']}
                            errorMessages={['First Name is required.', "First Name should not exceed 50 characters."]}
                            value={data.firstName}
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextValidatorHcs
                            key="lastName"
                            name="lastName"
                            label="Last Name"
                            required
                            fullWidth
                            validators={['required', 'maxStringLength:50']}
                            errorMessages={['Last Name is required.', "Last Name should not exceed 50 characters."]}
                            value={data.lastName}
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextValidatorHcs
                            key="dateOfBirth"
                            name="dateOfBirth"
                            label="Date Of Birth"
                            fullWidth
                            required
                            type="date"
                            value={data.dateOfBirth}
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextValidatorHcs
                            type="phone"
                            key="Phone"
                            name="phone"
                            label="Phone"
                            fullWidth
                            value={data.phone}
                            onChange={handleChange}
                            validators={!!data.phone ? ['isPhoneNumber'] : []}
                            errorMessages={!!data.phone ? ['Invalid phone number'] : []}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextValidatorHcs
                            key="address"
                            name="address"
                            label="Address"
                            fullWidth
                            required
                            withRequiredValidator
                            validators={['required']}
                            errorMessages={['Address is required.']}
                            value={data.address}
                            onChange={handleChange}
                            multiline
                            rows={3}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextValidatorHcs
                            key="county"
                            name="county"
                            label="County"
                            fullWidth
                            required
                            withRequiredValidator
                            validators={['required']}
                            errorMessages={['County is required.']}
                            value={data.county}
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextValidatorHcs
                            key="postalCode"
                            name="postalCode"
                            label="Postal Code"
                            fullWidth
                            required
                            validators={['required', 'minStringLength:4', 'maxStringLength:10']}
                            errorMessages={['Postal Code is required.', "Postal code must be at least 4 characters long.", "Postal code should not exceed 10 characters."]}
                            value={data.postalCode}
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextValidatorHcs
                            key="city"
                            name="city"
                            label="City"
                            fullWidth
                            required
                            withRequiredValidator
                            validators={['required']}
                            errorMessages={['City is required.']}
                            value={data.city}
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth required>
                            <InputLabel>Country</InputLabel>
                            <Select MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 300,
                                        paddingTop: '10px',
                                        paddingBottom: '10px'
                                    },
                                },
                            }}
                                name="country"
                                required
                                value={data.country}
                                onChange={handleChange}
                                label="Select Country"
                            >
                                {Object.keys(CountryEnum).map(country => (
                                    <MenuItem key={country} value={country}>{country}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <TextValidatorHcs
                            type="email"
                            key="email"
                            name="email"
                            label="Email"
                            required
                            fullWidth
                            validators={['required', 'isEmailAddress']}
                            errorMessages={['Email is required.', 'Invalid Email Address']}
                            value={data.email}
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextValidatorHcs
                            required
                            fullWidth
                            label="Initial Password"
                            name="password"
                            value={data.password}
                            onChange={handleChange}
                            type="password"
                            validators={RESET_PASSWORD_VALIDATORS(true)}
                            errorMessages={RESET_PASSWORD_ERROR_MESSAGES(true)}
                            helperText={!data.password ? RESET_PASSWORD_HELPER_TEXT : `${'\u2713'} Password is valid`}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextValidatorHcs
                            error={data.confirmPassword && data.password !== data.confirmPassword}
                            required
                            fullWidth
                            name="confirmPassword"
                            label="Confirm Password"
                            type="password"
                            value={data.confirmPassword}
                            withRequiredValidator
                            onChange={handleChange}
                            validators={[
                                `required`,
                                `minStringLength:7`,
                            ]}
                            errorMessages={['Confirm Password field is required', 'This field must be at least 7 characters long']}
                            autoComplete={"off"}
                            helperText={data.password && data.confirmPassword && data.password === data.confirmPassword ? `${'\u2713'} Passwords match` : data.confirmPassword ? "Passwords don't match" : ""}
                        />

                    </Grid>

                    <Grid container justifyContent="center" margin={1}>
                        <PrimaryButton
                            isSaving={isSaving}
                            type="submit"
                            width="140px"
                            variant="success">{"Save"}
                        </PrimaryButton>
                    </Grid>

                </Grid>
            </ValidatorForm>

        </Container >
    );

}
